import { useState } from 'react';
import { Save01, Eye, EyeOff } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { useChangePassword } from '@/hooks/api/use-profile';
import { useTranslation } from 'react-i18next';

export const ProfilePasswordForm = () => {
    const { t } = useTranslation(['pages:profile', 'common']);
    const changePassword = useChangePassword();
    
    const [formData, setFormData] = useState({
        current_password: '',
        new_password: '',
        confirm_password: '',
    });

    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false,
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (formData.new_password !== formData.confirm_password) {
            // Error will be shown by the hook
            return;
        }

        await changePassword.mutateAsync({
            current_password: formData.current_password,
            new_password: formData.new_password,
            confirm_password: formData.confirm_password,
        });

        // Reset form on success
        setFormData({
            current_password: '',
            new_password: '',
            confirm_password: '',
        });
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="relative">
                <Input
                    label={t('password.currentPassword')}
                    placeholder={t('password.currentPasswordPlaceholder')}
                    value={formData.current_password}
                    onChange={(value) => setFormData({ ...formData, current_password: value })}
                    type={showPasswords.current ? 'text' : 'password'}
                    size="md"
                    isRequired
                />
                <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                    className="absolute right-3 top-8 text-tertiary hover:text-primary"
                >
                    {showPasswords.current ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
                </button>
            </div>

            <div className="relative">
                <Input
                    label={t('password.newPassword')}
                    placeholder={t('password.newPasswordPlaceholder')}
                    value={formData.new_password}
                    onChange={(value) => setFormData({ ...formData, new_password: value })}
                    type={showPasswords.new ? 'text' : 'password'}
                    size="md"
                    isRequired
                />
                <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                    className="absolute right-3 top-8 text-tertiary hover:text-primary"
                >
                    {showPasswords.new ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
                </button>
            </div>

            <div className="relative">
                <Input
                    label={t('password.confirmPassword')}
                    placeholder={t('password.confirmPasswordPlaceholder')}
                    value={formData.confirm_password}
                    onChange={(value) => setFormData({ ...formData, confirm_password: value })}
                    type={showPasswords.confirm ? 'text' : 'password'}
                    size="md"
                    isRequired
                    isInvalid={formData.new_password !== formData.confirm_password && formData.confirm_password.length > 0}
                />
                <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                    className="absolute right-3 top-8 text-tertiary hover:text-primary"
                >
                    {showPasswords.confirm ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
                </button>
            </div>

            {formData.new_password !== formData.confirm_password && formData.confirm_password.length > 0 && (
                <p className="text-sm text-error-600">{t('password.mismatch')}</p>
            )}

            <div className="flex items-center justify-end gap-4 pt-4 border-t border-secondary">
                <Button
                    type="submit"
                    color="primary"
                    size="md"
                    iconLeading={Save01}
                    isDisabled={changePassword.isPending}
                >
                    {changePassword.isPending ? t('common:saving') : t('password.changePassword')}
                </Button>
            </div>
        </form>
    );
};

